'use client';

import Link from '@/routing/AppLink';
import { useEffect, useRef, useState } from 'react';

import {
  FAQ_PAGE_COPY,
  getFaqGroups,
  matchesFaq,
} from '@/data/shared/faqDirectory';

import ResponsiveLayout from '@/components/apps/layouts/responsive/Layout';
import Area from '@/components/apps/layouts/shared/Area';
import FaqDirectoryItem from '@/components/apps/pages/faq/FaqDirectoryItem';
import { trackFaqEvent } from '@/components/apps/pages/faq/faqAnalytics';
import { useFaqAnalytics } from '@/components/apps/pages/faq/useFaqAnalytics';

import type { LanguageType } from '@/types/language';

const focusClass =
  'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-apps-corePrimary01';

export default function FaqPageClient({ locale }: { locale: LanguageType }) {
  const copy = FAQ_PAGE_COPY[locale];
  const groups = getFaqGroups(locale);
  const [category, setCategory] = useState('all');
  const [query, setQuery] = useState('');
  const pendingScroll = useRef<string | null>(null);
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      entries: group.entries.filter((entry) => matchesFaq(entry, query)),
    }))
    .filter((group) => group.entries.length > 0);
  const count = filteredGroups.reduce(
    (sum, group) => sum + group.entries.length,
    0,
  );
  useFaqAnalytics(locale, query, count);
  const categories = [{ id: 'all', title: copy.all }, ...groups];

  useEffect(() => {
    if (!pendingScroll.current) return;
    scrollToFaq(pendingScroll.current);
    pendingScroll.current = null;
  }, [query]);

  const navigateToCategory = (id: string) => {
    trackFaqEvent('faq_topic_select', locale, { topic_id: id });
    setCategory(id);
    const targetId = id === 'all' ? 'faq-list' : `faq-${id}`;
    // Restore answers before scrolling if search has hidden the destination.
    if (query && (id === 'all' || !document.getElementById(targetId))) {
      pendingScroll.current = targetId;
      setQuery('');
      return;
    }
    scrollToFaq(targetId);
  };

  return (
    <ResponsiveLayout mobilePadding={{ top: '0', bottom: '0' }}>
      <section className="bg-apps-coreGray07 py-16 lg:py-24">
        <Area>
          <div className="mx-auto max-w-[1040px]">
            <p className="mb-5 text-sm font-bold tracking-[0.16em] text-apps-corePrimary01">
              FAQ
            </p>
            <h1 className="text-4xl font-bold leading-tight tracking-tight text-apps-coreGrayBlack lg:text-6xl">
              {copy.title}
            </h1>
            <p className="mt-6 max-w-[680px] text-lg leading-relaxed text-apps-coreGray02">
              {copy.description}
            </p>
            <div className="relative mt-10 max-w-[680px]">
              <label htmlFor="faq-search" className="sr-only">
                {copy.search}
              </label>
              <input
                id="faq-search"
                type="search"
                aria-label={copy.search}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={copy.placeholder}
                className={`w-full rounded-xl border border-apps-coreProductDivider bg-apps-coreGrayWhite py-5 pl-6 pr-20 text-base text-apps-coreGrayBlack ${focusClass}`}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  aria-label={copy.clear}
                  className={`absolute top-1/2 right-4 -translate-y-1/2 rounded-lg px-3 py-2 text-xl text-apps-coreGray02 ${focusClass}`}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </Area>
      </section>
      <Area>
        <div className="mx-auto max-w-[1040px] py-12 lg:py-20">
          <div
            role="group"
            aria-label={copy.categories}
            className="flex flex-wrap gap-2"
          >
            {categories.map((item) => (
              <button
                type="button"
                key={item.id}
                aria-pressed={category === item.id}
                onClick={() => navigateToCategory(item.id)}
                className={`cursor-pointer rounded-full border px-5 py-3 text-sm font-bold ${focusClass} ${category === item.id ? 'border-apps-coreGrayBlack bg-apps-coreGrayBlack text-apps-coreGrayWhite' : 'border-apps-coreProductDivider text-apps-coreGray02'}`}
              >
                {item.title}
              </button>
            ))}
          </div>
          <p
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-8 text-sm text-apps-coreGray02"
          >
            {copy.count(count)}
          </p>
          <div
            id="faq-list"
            tabIndex={-1}
            className="mt-8 scroll-mt-[84px] space-y-16 outline-none lg:scroll-mt-[124px]"
          >
            {filteredGroups.map((group) => (
              <section key={group.id} aria-labelledby={`faq-${group.id}`}>
                <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
                  <h2
                    id={`faq-${group.id}`}
                    tabIndex={-1}
                    className="scroll-mt-[84px] text-2xl font-bold text-apps-coreGrayBlack outline-none lg:scroll-mt-[124px]"
                  >
                    {group.title}
                  </h2>
                  {group.id !== 'general' && (
                    <Link
                      href={group.href}
                      onClick={() =>
                        trackFaqEvent('faq_solution_click', locale, {
                          topic_id: group.id,
                        })
                      }
                      className={`text-sm font-medium text-apps-corePrimary01 ${focusClass}`}
                    >
                      {copy.solution} →
                    </Link>
                  )}
                </div>
                {group.entries.map((entry) => (
                  <FaqDirectoryItem
                    key={entry.question}
                    locale={locale}
                    entry={entry}
                    groupId={group.id}
                  />
                ))}
              </section>
            ))}
            {count === 0 && (
              <div className="rounded-2xl bg-apps-coreGray07 px-6 py-16 text-center">
                <h2 className="text-xl font-bold text-apps-coreGrayBlack">
                  {copy.empty}
                </h2>
                <p className="mt-3 text-apps-coreGray02">{copy.emptyHelp}</p>
                <Link
                  href="/inquiry"
                  onClick={() =>
                    trackFaqEvent('faq_inquiry_click', locale, {
                      context: 'search_empty',
                    })
                  }
                  className={`mr-6 font-bold text-apps-corePrimary01 ${focusClass}`}
                >
                  {copy.contact}
                </Link>
                <button
                  type="button"
                  className={`mt-6 font-bold text-apps-corePrimary01 ${focusClass}`}
                  onClick={() => {
                    setCategory('all');
                    setQuery('');
                  }}
                >
                  {copy.reset}
                </button>
              </div>
            )}
          </div>
        </div>
      </Area>
      <section className="bg-apps-coreGray07 py-16 lg:py-20">
        <Area>
          <div className="mx-auto max-w-[1040px]">
            <h2 className="text-2xl font-bold text-apps-coreGrayBlack lg:text-3xl">
              {copy.contactTitle}
            </h2>
            <p className="mt-4 text-base leading-relaxed text-apps-coreGray02">
              {copy.contactDescription}
            </p>
            <Link
              href="/inquiry"
              onClick={() =>
                trackFaqEvent('faq_inquiry_click', locale, {
                  context: 'page_footer',
                })
              }
              className={`mt-8 inline-flex rounded-full bg-apps-corePrimary01 px-7 py-4 font-bold text-apps-coreGrayWhite ${focusClass}`}
            >
              {copy.contact} →
            </Link>
          </div>
        </Area>
      </section>
    </ResponsiveLayout>
  );
}

function scrollToFaq(id: string) {
  const target = document.getElementById(id);
  if (!target) return;
  target.scrollIntoView({
    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 'instant'
      : 'smooth',
    block: 'start',
  });
  target.focus({ preventScroll: true });
}
